import {
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
} from '../icons';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  Alert,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import menuService from '../../services/menuService';
import orderService from '../../services/orderService';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';

const AddOrderItemDialog = ({ open, onClose, orderId, onItemAdded }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { formatCurrency } = useCurrencyFormatter();

  const [searchQuery, setSearchQuery] = useState('');
  const [allMenuItems, setAllMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [selectedOptions, setSelectedOptions] = useState({});

  const fetchMenuItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await menuService.getMenuItems({ is_available: true });
      setAllMenuItems(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Error fetching menu items:', err);
      setError('Failed to load menu items.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchMenuItems();
      setSearchQuery('');
      setSelectedItem(null);
      setQuantity(1);
      setNotes('');
      setSelectedOptions({});
      setError(null);
    }
  }, [open, fetchMenuItems]);

  const menuItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return allMenuItems;
    return allMenuItems.filter((item) =>
      item.name?.toLowerCase().includes(query)
    );
  }, [searchQuery, allMenuItems]);

  const getOptionGroups = (item) => {
    if (!item?.option_groups || !Array.isArray(item.option_groups)) return [];
    return item.option_groups
      .map((link) => link.option_group)
      .filter((og) => og && og.is_active !== false);
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setQuantity(1);
    setNotes('');
    setSelectedOptions({});
  };

  const handleSingleOptionChange = (groupId, optionId) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [groupId]: [optionId],
    }));
  };

  const handleMultipleOptionChange = (groupId, optionId, checked) => {
    setSelectedOptions((prev) => {
      const current = prev[groupId] || [];
      const updated = checked
        ? [...current, optionId]
        : current.filter((id) => id !== optionId);
      return { ...prev, [groupId]: updated };
    });
  };

  const getSelectedOptionIds = () => {
    return Object.values(selectedOptions).flat();
  };

  const getOptionsTotal = () => {
    if (!selectedItem) return 0;
    const groups = getOptionGroups(selectedItem);
    let total = 0;
    for (const group of groups) {
      const selected = selectedOptions[group.id] || [];
      for (const item of group.items || []) {
        if (selected.includes(item.id)) {
          total += Number(item.price_addition) || 0;
        }
      }
    }
    return total;
  };

  const getItemTotal = () => {
    if (!selectedItem) return 0;
    const basePrice = Number(selectedItem.price) || 0;
    const optionsPrice = getOptionsTotal();
    return (basePrice + optionsPrice) * quantity;
  };

  const hasRequiredOptionsSelected = () => {
    if (!selectedItem) return true;
    const groups = getOptionGroups(selectedItem);
    for (const group of groups) {
      if (group.is_required) {
        const selected = selectedOptions[group.id] || [];
        if (selected.length === 0) return false;
      }
    }
    return true;
  };

  const handleAddItem = async () => {
    if (!selectedItem || !orderId) return;
    if (!hasRequiredOptionsSelected()) {
      setError('Please select all required options.');
      return;
    }
    try {
      setAdding(selectedItem.id);
      setError(null);
      const optionIds = getSelectedOptionIds();
      await orderService.createOrderItem({
        order: orderId,
        menu_item: selectedItem.id,
        name: selectedItem.name,
        description: selectedItem.description || '',
        price: selectedItem.price,
        quantity,
        notes,
        ...(optionIds.length > 0 && { options: optionIds }),
      });
      if (onItemAdded) onItemAdded();
      setSelectedItem(null);
      setQuantity(1);
      setNotes('');
      setSelectedOptions({});
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to add item to order.');
    } finally {
      setAdding(null);
    }
  };

  const renderOptionGroups = () => {
    if (!selectedItem) return null;
    const groups = getOptionGroups(selectedItem);
    if (groups.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        {groups.map((group) => (
          <Box
            key={group.id}
            sx={{
              mb: 2,
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              p: 2,
              border: group.is_required && !(selectedOptions[group.id]?.length)
                ? '1px solid #fca5a5'
                : '1px solid transparent',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937' }}>
                {group.name}
              </Typography>
              {group.is_required && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    bgcolor: '#fef2f2',
                    color: '#dc2626',
                    border: '1px solid #fecaca',
                  }}
                />
              )}
              <Typography variant="caption" sx={{ color: '#9ca3af', ml: 'auto' }}>
                {group.selection_type === 'SINGLE' ? 'Choose one' : 'Choose multiple'}
              </Typography>
            </Box>

            {group.selection_type === 'SINGLE' ? (
              <RadioGroup
                value={(selectedOptions[group.id] || [])[0] || ''}
                onChange={(e) => handleSingleOptionChange(group.id, e.target.value)}
              >
                {(group.items || [])
                  .filter((opt) => opt.is_active !== false)
                  .map((opt) => (
                    <FormControlLabel
                      key={opt.id}
                      value={opt.id}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: '#667eea',
                            '&.Mui-checked': { color: '#667eea' },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                            {opt.name}
                          </Typography>
                          {Number(opt.price_addition) > 0 && (
                            <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 600 }}>
                              +{formatCurrency(Number(opt.price_addition))}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ mx: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                    />
                  ))}
              </RadioGroup>
            ) : (
              <FormGroup>
                {(group.items || [])
                  .filter((opt) => opt.is_active !== false)
                  .map((opt) => (
                    <FormControlLabel
                      key={opt.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={(selectedOptions[group.id] || []).includes(opt.id)}
                          onChange={(e) =>
                            handleMultipleOptionChange(group.id, opt.id, e.target.checked)
                          }
                          sx={{
                            color: '#667eea',
                            '&.Mui-checked': { color: '#667eea' },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                            {opt.name}
                          </Typography>
                          {Number(opt.price_addition) > 0 && (
                            <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 600 }}>
                              +{formatCurrency(Number(opt.price_addition))}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ mx: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                    />
                  ))}
              </FormGroup>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '20px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          maxHeight: isMobile ? '100%' : '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: '#667eea',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.3rem',
          py: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {selectedItem ? 'Confirm Item' : 'Add Item to Order'}
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {selectedItem ? (
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                bgcolor: '#f8fafc',
                borderRadius: '12px',
                p: 2.5,
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
                {selectedItem.name}
              </Typography>
              {selectedItem.description && (
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                  {selectedItem.description}
                </Typography>
              )}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  background: '#667eea',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {formatCurrency(Number(selectedItem.price))}
              </Typography>
            </Box>

            {renderOptionGroups()}

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                Quantity
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    width: 40,
                    height: 40,
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 30, textAlign: 'center' }}>
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => setQuantity((q) => q + 1)}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    width: 40,
                    height: 40,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ color: '#6b7280', ml: 'auto' }}>
                  Total: {formatCurrency(getItemTotal())}
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Special instructions..."
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              }}
            />

            <Button
              fullWidth
              variant="text"
              onClick={() => setSelectedItem(null)}
              sx={{ mt: 2, textTransform: 'none', color: '#6b7280' }}
            >
              Back to search
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ p: 2, pb: 1 }}>
              <TextField
                fullWidth
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                }}
              />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : menuItems.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: '#6b7280', textAlign: 'center', py: 6 }}
                >
                  {searchQuery ? 'No items found' : 'No available items'}
                </Typography>
              ) : (
                menuItems.map((item) => {
                  const optionCount = getOptionGroups(item).length;
                  return (
                    <Box
                      key={item.id}
                      onClick={() => handleSelectItem(item)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        mb: 1,
                        bgcolor: '#f8fafc',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        border: '1px solid transparent',
                        '&:hover': {
                          bgcolor: '#eef2ff',
                          borderColor: '#667eea',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f2937' }}>
                          {item.name}
                        </Typography>
                        {item.description && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6b7280',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {item.category_name && (
                            <Chip
                              label={item.category_name}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                bgcolor: '#e0e7ff',
                                color: '#4338ca',
                              }}
                            />
                          )}
                          {optionCount > 0 && (
                            <Chip
                              label={`${optionCount} option${optionCount > 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                bgcolor: '#f0fdf4',
                                color: '#16a34a',
                                border: '1px solid #bbf7d0',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea', whiteSpace: 'nowrap' }}>
                        {formatCurrency(Number(item.price))}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </>
        )}
      </DialogContent>

      {selectedItem && (
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <Button
            onClick={onClose}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={!!adding || !hasRequiredOptionsSelected()}
            startIcon={adding ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            sx={{
              background: '#667eea',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              borderRadius: '10px',
              '&:hover': {
                background: '#5568d3',
              },
            }}
          >
            {adding ? 'Adding...' : `Add Item${getOptionsTotal() > 0 ? ` (${formatCurrency(getItemTotal())})` : ''}`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AddOrderItemDialog;
